// ── what leaves the browser about WHERE someone is ───────────────────────────
//
// Two things ride in a location and neither belongs at a third party:
//
//   1. credentials. Auth can put a secret in a url (a PKCE `?code=`, a magic
//      link's `#access_token=…`; AuthHashCatch runs on every page for exactly
//      that). PostHog's $pageview captures the full href INCLUDING the hash.
//   2. places. On the stage a url holds the slug of a dimension's title
//      (placePath.ts says how, and what a place is reduced to).
//
// So every location that is about to be sent goes through `scrubUrl`, and every
// string that might hold one (an autocapture chain, an exception message, a
// breadcrumb) through `templatePlacesInText`. UTM stays: that attribution is the
// whole point of the pageview.
//
// This is the machinery behind instrumentation-client.ts's `before_send`,
// `beforeBreadcrumb` and replay masks, and it lives here, not there, because that
// module calls posthog.init() on import (see placePath.ts, replayMask.ts).
//
// Every hook fails CLOSED. If an event is too deep or too wide to be checked in
// full it is dropped (null), not sent half-checked: posthog-js and Sentry both
// treat a null from these hooks as "do not send".

import { templateNodeKey, templatePlacePath, templatePlacesInText, templateShellPath } from './placePath'
import { REPLAY_KEY_ATTRIBUTES, REPLAY_WORD_ATTRIBUTES } from './replayMask'

const SENSITIVE_QS = new Set([
  'code', 'token', 'token_hash', 'access_token', 'refresh_token',
  'provider_token', 'provider_refresh_token', 'otp', 'type', 'email', 'secret',
  // X's ad click id is for X alone (lib/ads/signal), never a product property
  'twclid',
])

/** posthog-js's own copies of X's click id: `twclid`, `$session_entry_twclid`, `$initial_twclid`. */
const TWCLID_KEY = /^(?:\$(?:initial_|session_entry_))?twclid$/i

/** the host a relative url is parsed against, so one parser handles both forms. */
const RELATIVE_BASE = 'http://relative.invalid'

/**
 * A url (absolute, or a relative path with a query) as it may be recorded: hash
 * dropped, the sensitive query keys stripped, a place reduced to its template:
 * in the path, and in EVERY query value (an arrival link's `?open=`, and the
 * `?next=` a signed-out visit to a place is sent to /login with). The host and
 * every query key, utm_* above all, are kept.
 */
export function scrubUrl(raw: string): string {
  try {
    const relative = raw.startsWith('/') && !raw.startsWith('//')
    const u = new URL(raw, relative ? RELATIVE_BASE : undefined)
    u.hash = ''
    for (const k of [...u.searchParams.keys()]) {
      if (SENSITIVE_QS.has(k.toLowerCase())) u.searchParams.delete(k)
    }
    // every value, not a list of keys: proxy.ts sends a signed-out visit to
    // `/login?next=<the place>`, and from there it hops on (/auth/land, /welcome)
    // under whatever name the next page gives it. A value comes back decoded, so
    // `next=%2Fdashboard%2F…` is a plain path by now.
    let changed = false
    const pairs = [...u.searchParams].map(([k, v]): [string, string] => {
      const t = k === 'open' ? templateShellPath(v) : templatePlacesInText(v)
      if (t !== v) changed = true
      return [k, t]
    })
    if (changed) u.search = new URLSearchParams(pairs).toString()
    u.pathname = templatePlacePath(u.pathname)
    return relative ? u.pathname + u.search : u.toString()
  } catch {
    // not a url at all ("$direct", a bare domain): still never ship a place in it
    return templatePlacesInText(raw.split('#')[0])
  }
}

// ── walking an event ─────────────────────────────────────────────────────────

type Bag = Record<string, unknown>
type Budget = { left: number }

const MAX_DEPTH = 12

/** plain data only: a breadcrumb can hold a DOM node or `window`, which has no business being walked. */
function isPlain(v: object): boolean {
  if (Array.isArray(v)) return true
  const proto = Object.getPrototypeOf(v)
  return proto === Object.prototype || proto === null
}

/** how one sink's keys are read: which hold a url, which are not walked, which are removed whole. */
type Rules = {
  isUrlKey: (k: string) => boolean
  skip: ReadonlySet<string>
  drop: (k: string) => boolean
}

/**
 * Rewrite every string under `node`, in place: a value under a url key through
 * `scrubUrl`, any other through `templatePlacesInText`, and a key the sink must
 * never see deleted, whatever it holds. False when it gave up (too deep, or past
 * its budget, which is also what a cycle looks like).
 */
function scrubStrings(node: Bag, rules: Rules, budget: Budget, depth: number): boolean {
  for (const k of Object.keys(node)) {
    if (rules.skip.has(k)) continue
    if (--budget.left < 0 || depth > MAX_DEPTH) return false
    if (rules.drop(k)) {
      delete node[k]
      continue
    }
    const v = node[k]
    if (typeof v === 'string') {
      node[k] = rules.isUrlKey(k) ? scrubUrl(v) : templatePlacesInText(v)
    } else if (v && typeof v === 'object' && isPlain(v) && !scrubStrings(v as Bag, rules, budget, depth + 1)) {
      return false
    }
  }
  return true
}

// ── PostHog ──────────────────────────────────────────────────────────────────

// Every url-valued property posthog-js stamps. Not just the pageview's own: the
// SESSION's entry url and the person's initial url ride along on every event
// (`$session_entry_url`, and in `$set_once` for an identified person), and a
// session that starts from a bookmarked place carries that place for its whole life.
const POSTHOG_URL_KEY =
  /^\$(?:initial_|session_entry_)?(?:current_url|url|pathname|referrer|referring_domain)$|^\$prev_pageview_pathname$|^\$external_click_url$/

// the replay payload is big and is masked at its source (the two replay hooks below).
const POSTHOG_RULES: Rules = {
  isUrlKey: (k) => POSTHOG_URL_KEY.test(k),
  skip: new Set(['$snapshot_data']),
  drop: () => false,
}

const POSTHOG_BUDGET = 10_000

type EventLike = {
  event?: string
  properties?: Record<string, unknown>
  $set?: Record<string, unknown>
  $set_once?: Record<string, unknown>
}

/**
 * posthog-js `before_send`: nothing but templated places and scrubbed urls
 * leaves. Returns the event, or null to drop it (see the header).
 */
export function scrubPostHogEvent<E extends EventLike | null | undefined>(event: E): E | null {
  if (!event) return event
  const props = event.properties
  if (props) {
    // the tab title never leaves, on any url. The stage names the tab after the
    // place (`<document> · your innernet`), and posthog-js reads document.title
    // inside the pushState it patches, before the next place has renamed the tab:
    // leave a document for /dashboard/projects (or Back out of it) and the
    // pageview carries a section's url with the document's name. A stale title
    // can sit on any url, so which url an event came from cannot say it is safe.
    delete props.title

    // a heatmap is keyed by the url it was drawn on
    const heat = props.$heatmap_data
    if (heat && typeof heat === 'object' && !Array.isArray(heat)) {
      const byUrl: Record<string, unknown> = {}
      for (const [url, points] of Object.entries(heat)) {
        const key = scrubUrl(url)
        const had = byUrl[key]
        byUrl[key] = Array.isArray(had) && Array.isArray(points) ? [...had, ...points] : points
      }
      props.$heatmap_data = byUrl
    }
  }
  // X's click id is X's alone (lib/ads/signal, /privacy). Stripping it from urls
  // was not enough: posthog-js lifts it out of the landing url into a campaign
  // property of its own (`twclid`, `$session_entry_twclid`, `$initial_twclid`),
  // and 2026-11-04 found it on every paid X visit. The id goes; the fact that the
  // visit came off an X ad stays, as `x_ad_click`. The server never reads it from
  // here: the conversion's click id comes from the browser's own store (lib/utm).
  let adClick = false
  for (const bag of [props, event.$set, event.$set_once]) {
    if (!bag || typeof bag !== 'object') continue
    for (const k of Object.keys(bag)) {
      if (TWCLID_KEY.test(k)) {
        if (bag[k]) adClick = true
        delete bag[k]
      }
    }
  }
  if (adClick && props) props.x_ad_click = true

  const budget = { left: POSTHOG_BUDGET }
  for (const bag of [props, event.$set, event.$set_once]) {
    if (bag && !scrubStrings(bag, POSTHOG_RULES, budget, 0)) return null
  }
  return event
}

const REPLAY_WORDS = new Set(REPLAY_WORD_ATTRIBUTES)
const REPLAY_KEYS = new Set(REPLAY_KEY_ATTRIBUTES)

/**
 * rrweb `maskAttributeFn`. A recording stores DOM attributes verbatim. The
 * block list keeps memory rows out (replayMask.ts), but the chrome around them
 * still carries a place: a link to one is `<a href="/dashboard/projects/<id>/
 * <title-slug>">`, a navigator word's `data-nav-key` is `dim:<title-slug>`, and
 * `data-hint` / `title` / `aria-label` carry names and summaries as words. So:
 * the word attributes become asterisks (what the text mask makes of text), a
 * key keeps its kind, a `data-*` path is templated as a shell path, and every
 * other value has its places templated. A long value (an inlined image, a
 * stylesheet) is passed over by that last step: no address is that long.
 */
export function maskReplayAttribute(name: string, value: string): string {
  const n = name.toLowerCase()
  if (REPLAY_WORDS.has(n)) return '*'.repeat(value.length)
  if (REPLAY_KEYS.has(n)) return templateNodeKey(value)
  if (value.length > 4096) return value
  const out = templatePlacesInText(value)
  // `data-node-path` and its kind hold a SHELL path (`/projects/<id>/<slug>`):
  // no /dashboard in front of it for the text pass to find
  if (out === value && n.startsWith('data-') && value.startsWith('/') && !value.startsWith('//')) return templateShellPath(value)
  return out
}

/**
 * posthog-js `maskCapturedNetworkRequestFn`, which the recorder also runs the
 * page's own url through (the `href` of every Meta event, and of the $pageview
 * it adds to a recording), so the recording's own address bar is scrubbed too.
 * (Setting one swaps posthog-js's default payload scrub for it; nothing is lost,
 * because instrumentation-client records no headers and no bodies at all.)
 */
export function maskReplayUrl<R extends { name?: string }>(request: R): R {
  return typeof request.name === 'string' ? { ...request, name: scrubUrl(request.name) } : request
}

// ── Sentry ───────────────────────────────────────────────────────────────────

// Sentry names its own: a navigation breadcrumb's `from` / `to`, a fetch's `url`,
// the request's `url` and `Referer`, the semantic-convention `url.full`.
const SENTRY_URL_KEY = /^(?:url|url\.full|http\.url|from|to|referer|referrer)$/i

// The proxy hands the stage page its place in two request headers of its own
// (lib/stage/place PLACE_HEADER, SEARCH_HEADER), and the server's Sentry copies
// every request header onto an error (event.request.headers) and a span
// (`http.request.header.x_innernet_place`). The place one is a SHELL path,
// `/projects/<id>/<title-slug>` with no /dashboard in front for the text pass to
// find, and the search one is the raw query. Neither is any use to an error
// report, so both go whole, in every spelling Sentry writes them.
const SENTRY_DROP_KEY = /(?:^|\.)x[-_]innernet[-_](?:place|search)$/i

const SENTRY_BUDGET = 20_000

const SENTRY_RULES: Rules = {
  isUrlKey: (k) => SENTRY_URL_KEY.test(k),
  skip: new Set(),
  drop: (k) => SENTRY_DROP_KEY.test(k),
}

function scrubSentry<T>(thing: T): T | null {
  if (!thing || typeof thing !== 'object') return thing
  return scrubStrings(thing as Bag, SENTRY_RULES, { left: SENTRY_BUDGET }, 0) ? thing : null
}

/** Sentry `beforeBreadcrumb`: a navigation breadcrumb's from / to is a place. */
export function scrubSentryBreadcrumb<B>(crumb: B): B | null {
  return scrubSentry(crumb)
}

/**
 * Sentry `beforeSend` and `beforeSendTransaction`: the request url and its
 * query string, the transaction's name (it is the pathname), the proxy's place
 * headers, and anything a breadcrumb or span carried that the breadcrumb hook
 * did not see.
 */
export function scrubSentryEvent<E>(event: E): E | null {
  return scrubSentry(event)
}
